<template>
	<div :style="`--offset: ${dotOffset}`" :class="{ start: start, end: end }" class="location-card">
		<div :class="{ start: start, end: end }" class="before-line">
			<slot name="beforeLine" />
		</div>

		<div class="line-wrapper">

			<div class="additional-text top left">
				<slot name="overLineLeft" />
			</div>
			<div class="additional-text top right">
				<slot name="overLineRight" />
			</div>

			<div
				:class="[{ start: start, middle: !start && !end, end: end }, primaryColor, `${secondaryColor}secondary`]"
				class="location-line"
			/>

			<div class="location-dot" />
			<div :class="[primaryColor, secondaryColor]" class="location-dot inner" />

			<div
				v-if="!end"
				class="location-dot wrapper-dot"
				@mouseover="showNewDot = true"
				@mouseleave="showNewDot = false"
			>
				<div
					v-show="showNewDot"
					:class="`location-dot new-dot `"
				>
					<t-icon-button
						icon="plus"
						type="bp"
						appearance="minimal"
						size="x-small"
						iconSize="x-small"
						color="neutral"
						class="input"
						tabIndex="-1"
						@click="$emit('click')"
					/>
				</div>
			</div>

			<div class="additional-text bottom left">
				<slot name="underLineLeft" />
			</div>
			<div class="additional-text bottom right">
				<slot name="underLineRight" />
			</div>

		</div>

		<div :class="{ start: start, end: end }" class="after-line">
			<slot name="afterLine" />
		</div>
	</div>
</template>

<script>
export default {
	name: 'LocationCard',
	props: {
		primaryColor: String,
		secondaryColor: String,
		start: Boolean,
		end: Boolean,
		dotOffset: {
			type: String,
			default: '50%'
		}
	},
	data() {
		return { showNewDot: false };
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

$offset: var(--offset);

.location-card {
	&.start {
		align-items: flex-start;
		display: flex;
		flex-direction: column;
		width: 100%;
	}
	&.end {
		align-items: flex-end;
		display: flex;
		flex-direction: column;
		width: 100%;
	}
}

.before-line, .after-line {
	padding: 10px 20px;
	&.start {
		padding-left: 0;
	}
	&.end {
		padding-right: 0;
	}
}

.before-line {
	height: 150px;
}

.after-line {
	padding-top: 15px
}

.line-wrapper {
	width: 100%;
	padding: 10px 0;
	position: relative;
}

.input {
	height: 100% !important;
	width: 100% !important;
	border-radius: 1000px !important;
}

.additional-text {
	@extend %text-small;
	position: absolute;
	font-weight: 600;
	$fontSize: 12px;
	$width: 55;
	width: #{$width}px;

	&.top {
		bottom: 18px;
		$width: 100;
		width: #{$width}px;
	}

	&.bottom {
		top: 18px;
	}

	&.left {
		left: calc(#{$offset} - #{$width + 15}px);
	}

	&.right {
		left: calc(#{$offset} + 15px);
	}
}

.location-dot {
	box-sizing: border-box;
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: space-around;
	background-color: get-color($neutral-scale, 'N4');
	border-radius: 1000px;
	padding: 2px;
	z-index: 1;
	$size: 20;
	width: #{$size}px;
	height: #{$size}px;
	$half: $size / 2;
	top: calc(50% - #{$half}px);
	left: calc(#{$offset} - #{$half}px);

	&.new-dot {
		$center: calc(50% - #{$half}px);
		top: $center;
		left: $center;
		z-index: 3;
	}

	&.inner {
		background-color: get-color($neutral-scale, 'N6');
		$size: 12;
		width: #{$size}px;
		height: #{$size}px;
		$half: $size / 2;
		top: calc(50% - #{$half}px);
		left: calc(#{$offset} - #{$half}px);
		z-index: 3;

		@each $key, $value in $palette {
		&.#{$key} {
			background-color: get-color($value, 'base');
		}
	}
	}

	&.wrapper-dot {
		$size: 100;
		width: #{$size}px;
		height: #{$size}px;
		$half: $size / 2;
		top: calc(50% - #{$half}px);
		left: calc(100% - #{$half}px);
		z-index: 3;
		background-color: rgba($color: get-color($neutral-scale, 'N4'), $alpha: 0);
	}
}

@mixin line($color, $size, $direction, $width) {
	content: '';
	position: absolute;
	width: $width;
	background-color: $color;
	height: #{$size}px;
	$half: $size / 2;
	$center: calc(50% - #{$half}px);
	top: $center;
	z-index: 2;
	@if $direction == 'right' {
		right: 0;
	} @else {
		left: 0;
	}
}

.location-line {
	position: relative;
	width: 100%;

	&.start {
		$width: calc(100% - #{$offset});
		&:before {
			@include line(get-color($neutral-scale, 'N3'), 4, 'right', $width);
		}
		@each $key, $value in $palette {
			&.#{$key}:before {
				@include line(get-color($value, 'base'), 4, 'right', $width);
			}
		}
	}

	&.middle {
		&:before {
			@include line(get-color($neutral-scale, 'N3'), 4, 'left', 50%);
		}
		@each $key, $value in $palette {
			&.#{$key}:before {
				@include line(get-color($value, 'base'), 4, 'left', 50%);
			}
		}
		&:after {
			@include line(get-color($neutral-scale, 'N3'), 4, 'right', 50%);
		}
		@each $key, $value in $palette {
			&.#{$key}secondary:after {
				@include line(get-color($value, 'base'), 4, 'right', 50%);
			}
		}
	}

	&.end {
		$width: $offset;
		&:before {
			@include line(get-color($neutral-scale, 'N3'), 4, 'left', $width);
		}
		@each $key, $value in $palette {
			&.#{$key}:before {
				@include line(get-color($value, 'base'), 4, 'left', $width);
			}
		}
	}
}
</style>